import cn from 'classnames';
import * as React from 'react';
import { RemoveScroll } from 'react-remove-scroll';

import Portal from 'components/ui/Portal';
import { PORTAL_CONTAINER_SELECTORS } from 'config/portalContainers';
import { useMeasure } from 'hooks/';

import s from './CustomDrawer.module.scss';
import { CustomDrawerBody, CustomDrawerBodyProps } from './CustomDrowerBody';
import { DrawerHeader } from './DrawerHeader';
import { useMounting } from './useMounting';

type CustomDrawerProps = React.PropsWithChildren<{
  onClick?: React.MouseEventHandler;
  mask?: boolean;
  bodyScroll?: boolean;
  opened: boolean;
  onClose: VoidFunction;
  title: React.ReactNode;
  extra: React.ReactNode;
  onChangeContentHeight?: (contentHeight: number) => void;
}>;

const CustomDrawer: React.FC<CustomDrawerProps> & {
  Body: React.ComponentType<CustomDrawerBodyProps>;
} = ({
  onClick,
  mask = true,
  bodyScroll,
  opened: outerOpened,
  onClose,
  title,
  extra,
  children,
  onChangeContentHeight,
}: CustomDrawerProps) => {
  const { mounted, finalOpened, handleTransitionEnd, handleClose } =
    useMounting({
      outerOpened,
      onClose,
    });

  const [headerRef, { height: headerHeight }] = useMeasure<HTMLElement>();

  const containerRef = React.useRef<HTMLDivElement | null>(null);

  const headerStubRef = React.useRef<HTMLDivElement | null>(null);

  const [bodyRef, { height: bodyHeight }] = useMeasure<HTMLDivElement>();

  const totalContentHeight = headerHeight + bodyHeight;

  React.useEffect(() => {
    onChangeContentHeight?.(totalContentHeight);

    if (!finalOpened) {
      onChangeContentHeight?.(0);
    }
  }, [totalContentHeight, onChangeContentHeight, finalOpened]);

  const [isFixedHeader, setIsFixedHeader] = React.useState(false);

  const handleContainerScroll = React.useCallback(() => {
    requestAnimationFrame((): void => {
      if (!headerStubRef.current) {
        return;
      }

      const { y } = headerStubRef.current.getBoundingClientRect();

      setIsFixedHeader(y < 0);
    });
  }, []);

  React.useLayoutEffect(() => {
    if (mounted) {
      handleContainerScroll();
    }

    if (!mounted) {
      setIsFixedHeader(false);
    }
  }, [mounted, handleContainerScroll]);

  const bodyStyle: React.CSSProperties = React.useMemo(
    () => ({
      marginTop: isFixedHeader ? headerHeight : 0,
    }),
    [isFixedHeader, headerHeight]
  );

  if (!mounted) {
    return null;
  }

  const RealRemoveScroll = mask ? RemoveScroll : React.Fragment;

  const drawerHeader = (
    <DrawerHeader
      className={cn(
        s.customDrawer__header,
        isFixedHeader
          ? s.customDrawer__header_fixed
          : s.customDrawer__header_static
      )}
      ref={headerRef}
      onClose={handleClose}
      title={title}
      extra={extra}
    />
  );

  return (
    <Portal portalHolder={PORTAL_CONTAINER_SELECTORS.drawer}>
      {isFixedHeader && drawerHeader}
      <RealRemoveScroll>
        <div
          onClick={onClick}
          ref={containerRef}
          onScroll={handleContainerScroll}
          onTransitionEnd={handleTransitionEnd}
          className={cn(
            s.customDrawer,
            finalOpened && s.customDrawer_opened,
            !mask && s.customDrawer_noMask
          )}
        >
          <div className={s.customDrawer__back} onClick={handleClose} />
          <div ref={headerStubRef} />
          {!isFixedHeader && drawerHeader}
          <div
            ref={bodyRef}
            style={bodyStyle}
            className={cn(
              s.customDrawer__body,
              bodyScroll && s.customDrawer__body_scroll
            )}
          >
            {children}
          </div>
        </div>
      </RealRemoveScroll>
    </Portal>
  );
};

CustomDrawer.Body = CustomDrawerBody;
CustomDrawer.Body.displayName = 'CustomerDrawer.Body';

export default CustomDrawer;
